import { Button, Toolbar, Typography, useMediaQuery } from "@mui/material";

import { autostoreTheme } from "@qubit/autoparts";

import { useTranslation } from "react-i18next";

import { useLocation, useNavigate, useParams } from "react-router-dom";

import { useAppDispatch, useAppSelector } from "~/app/store";

import { MoveInventoryButton } from "~/features/inventory/MoveInventoryButton";
import { NavBarButtonGroup, UnselectButton } from "~/features/navbar/Navbar";

import {
  clearSelectedInventoryId,
  clearSelectedVariant,
  getInventoryReport
} from "~/redux/actions/inventory";

import { selectInventorySummaryToDisplay } from "~/redux/selectors/manualInventorySelectors";
import { InventorySummaryDto } from "~/types/api";

import {
  setIsAddPanelOpen,
  setIsAdjustPanelOpen,
  setIsBinHoldModalOpen
} from "./manualInventory.slice";

import { getViewType } from "./viewType";

type Props = {
  handleGetInventorySummaries: (args: {
    variantId?: Guid;
    binId?: Guid;
    offsetZero?: boolean;
  }) => void;
  resetPage: () => void;
};

export function ManualInventoryToolbar({
  handleGetInventorySummaries,
  resetPage
}: Props) {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const locationInfo = useLocation();
  const { pathname, search } = locationInfo;
  const { variantId: variantIdParam, binId: binIdParam } = useParams<{
    variantId?: string;
    binId?: string;
  }>();

  const isMobile = useMediaQuery(autostoreTheme.breakpoints.down("sm"));

  const inventorySummaryToDisplay = useAppSelector(
    selectInventorySummaryToDisplay
  );

  const selectedRows = useAppSelector(
    (state) => state.manualInventory.selectedRows
  );

  const selectedVariant = useAppSelector(
    (state) => state.inventory.selectedVariant
  );

  const isAnySelect: boolean = selectedRows.length >= 1;
  const isSingleSelect: boolean = selectedRows.length === 1;
  const singleSelectSummary: InventorySummaryDto | null =
    (isSingleSelect && inventorySummaryToDisplay) || null;

  const viewType = getViewType(pathname);
  const isBinView = viewType === "bin";
  const isProductView = viewType === "product";
  const isProductsView = viewType === "products";

  const handleSearchSelected = () => {
    if (["bin", "products"].includes(viewType) && selectedVariant) {
      navigate({
        pathname: `/manual-inventory/product/${selectedVariant.variantId}`,
        search
      });
    } else if (isProductView && singleSelectSummary) {
      navigate({
        pathname: `/manual-inventory/bin/${singleSelectSummary.binId}`,
        search
      });
    }
  };

  return (
    <Toolbar sx={{ pl: isMobile ? 3 : 2 }}>
      {isAnySelect && (
        <>
          <UnselectButton onClick={resetPage} />
          <NavBarButtonGroup>
            {/* is manual ops || is not fetching : create inventory (1 item) */}
            {isSingleSelect && (
              <Button
                color="secondary"
                onClick={() => {
                  dispatch(setIsAddPanelOpen(true));
                }}
                sx={{
                  flexBasis: "auto",
                  width: "175px"
                }}
              >
                <Typography variant="body2" style={{ color: "#fff" }}>
                  {t("create inventory")}
                </Typography>
              </Button>
            )}
            {/* is selected && manual ops : adjust product directly. */}
            {isSingleSelect && singleSelectSummary?.binType !== "autostore" && (
              <Button
                color="primary"
                id="clear-batch-id"
                onClick={(): void => {
                  dispatch(setIsAdjustPanelOpen(true));
                }}
                sx={{
                  flexBasis: "auto",
                  width: "175px"
                }}
              >
                <Typography variant="body2" style={{ color: "#fff" }}>
                  {t("adjust")}
                </Typography>
              </Button>
            )}
            {/* is selected && can move inventory : move */}
            {isSingleSelect && inventorySummaryToDisplay && (
              <MoveInventoryButton
                invToModify={inventorySummaryToDisplay}
                refreshCb={async ({ inventoryWasEmptied }) => {
                  if (isProductView) {
                    handleGetInventorySummaries({
                      variantId: variantIdParam
                    });
                    dispatch(clearSelectedInventoryId());
                  }
                  if (isBinView) {
                    handleGetInventorySummaries({
                      binId: binIdParam
                    });
                  }

                  if (isProductsView) {
                    handleGetInventorySummaries({});
                  }

                  if (inventoryWasEmptied && isBinView) {
                    dispatch(clearSelectedVariant());
                  }

                  if (selectedVariant) {
                    await dispatch(
                      getInventoryReport(selectedVariant.variantId)
                    );
                  }
                }}
              />
            )}
            {/* is selected : search product/bin */}
            {isSingleSelect && (
              <Button
                onClick={(): void => {
                  handleSearchSelected();
                }}
                style={{
                  flexBasis: "auto",
                  width: "175px"
                }}
              >
                <Typography variant="body2" style={{ color: "#fff" }}>
                  {`${t("search")} ${
                    isBinView || isProductsView ? t("product") : t("bin")
                  }`}
                </Typography>
              </Button>
            )}
            {isAnySelect && (
              <Button
                id="add-holds-button"
                color="secondary"
                onClick={() => dispatch(setIsBinHoldModalOpen(true))}
                sx={{
                  flexBasis: "auto",
                  width: "175px"
                }}
              >
                <Typography variant="body2" style={{ color: "#fff" }}>
                  {t("nav.link.inventory holds")}
                </Typography>
              </Button>
            )}
          </NavBarButtonGroup>
        </>
      )}
    </Toolbar>
  );
}
